// Servicio para manejar el sistema de aprobación de actas
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp, getDoc, addDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getProjectEmailWithFallback, getContractProjectEmailWithFallback } from './projectsApi';
import { sendApprovalNotification, sendActaCreationNotification, sendActaApprovedNotification } from './emailService';
import { ROLES } from '../userRoles';
import { logUserAction, LOG_ACTIONS } from './logService';
import { hasBusinessDaysPassed, addBusinessDays } from '../utils/colombianHolidays';

// Estados de aprobación
export const APPROVAL_STATUS = {
  PENDING: 'por aprobar',
  APPROVED: 'aprobado'
};

/**
 * Obtiene el nombre de visualización de un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<string>} - Nombre del usuario o ID si no se encuentra
 */
const getUserDisplayName = async (userId) => {
  try {
    const userRef = doc(db, 'useractas', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      return userData.displayName || userData.email || userId;
    }
    
    return userId;
  } catch (error) {
    console.error('Error al obtener nombre del usuario:', error);
    return userId;
  }
};

/**
 * Verifica las firmas pendientes y las aprueba automáticamente por tiempo límite (solo para firmas)
 * @returns {Promise<{signaturesAutoApproved: number}>} - Número de firmas auto-aprobadas
 */
export const checkPendingSignatures = async () => {
  try {
    console.log('🔄 Verificando firmas pendientes...');
    
    // Obtener todas las actas que tienen firmas pendientes
    const actasRef = collection(db, 'actas');
    const q = query(actasRef, where('approvalStatus', '==', APPROVAL_STATUS.APPROVED));
    const querySnapshot = await getDocs(q);
    
    let signaturesAutoApproved = 0;
    
    // Verificar cada acta aprobada para firmas pendientes
    for (const docSnapshot of querySnapshot.docs) {
      const acta = {
        id: docSnapshot.id,
        ...docSnapshot.data(),
        fecha: docSnapshot.data().fecha ? docSnapshot.data().fecha.toDate() : new Date()
      };
      
      // Solo procesar actas que tienen asistentes con firmas pendientes
      if (!acta.asistentes || acta.asistentes.length === 0) continue;
      
      const hasPendingSignatures = acta.asistentes.some(asistente => 
        !asistente.firma && !asistente.comentario && !asistente.autoApproved
      );
      
      if (!hasPendingSignatures) continue;
      
      // Usar la fecha de aprobación como referencia para firmas
      const referenceDate = acta.approvedAt ? acta.approvedAt.toDate() : 
                           (acta.createdAt ? acta.createdAt.toDate() : new Date());
      
      // Verificar si han pasado 2 días hábiles desde la aprobación para firmas
      const hasSignatureTimeoutPassed = hasBusinessDaysPassed(referenceDate, 2);
      
      if (hasSignatureTimeoutPassed) {
        console.log(`⏰ Auto-aprobando firmas pendientes para acta ${acta.numeroActa}`);
        
        // Marcar solo las firmas pendientes como auto-aprobadas
        const updatedAsistentes = acta.asistentes.map(asistente => {
          if (!asistente.firma && !asistente.comentario && !asistente.autoApproved) {
            signaturesAutoApproved++;
            return {
              ...asistente,
              autoApproved: true,
              fechaFirma: Timestamp.now(),
              tipoFirma: 'auto_approved'
            };
          }
          return asistente;
        });
        
        // Actualizar solo los asistentes, NO el approvalStatus del acta
        await updateDoc(doc(db, 'actas', acta.id), {
          asistentes: updatedAsistentes,
          signaturesAutoApprovedAt: Timestamp.now()
        });
        
        // Registrar la acción en los logs
        await logUserAction(
          'system',
          LOG_ACTIONS.SIGNATURES_AUTO_APPROVED || 'SIGNATURES_AUTO_APPROVED',
          `Firmas pendientes auto-aprobadas para acta ${acta.numeroActa}`,
          { actaId: acta.id, numeroActa: acta.numeroActa }
        );
        
        console.log(`✅ Firmas auto-aprobadas para acta ${acta.numeroActa}`);
      }
    }
    
    console.log(`✅ Verificación de firmas completada. ${signaturesAutoApproved} firmas auto-aprobadas.`);
    return { signaturesAutoApproved };
  } catch (error) {
    console.error('Error al verificar firmas pendientes:', error);
    return { signaturesAutoApproved: 0 };
  }
};

/**
 * Verifica las actas pendientes de aprobación y envía notificaciones (SIN auto-aprobación)
 * @returns {Promise<{notificationsSent: number}>} - Número de notificaciones enviadas
 */
export const checkPendingApprovals = async () => {
  try {
    console.log('🔄 Verificando actas pendientes de aprobación...');
    
    // Obtener todas las actas pendientes de aprobación
    const actasRef = collection(db, 'actas');
    const q = query(actasRef, where('approvalStatus', '==', APPROVAL_STATUS.PENDING));
    const querySnapshot = await getDocs(q);
    
    let notificationsSent = 0;
    
    // Verificar cada acta pendiente
    for (const docSnapshot of querySnapshot.docs) {
      const acta = {
        id: docSnapshot.id,
        ...docSnapshot.data(),
        fecha: docSnapshot.data().fecha ? docSnapshot.data().fecha.toDate() : new Date()
      };
      
      // Usar la fecha de creación o envío para aprobación como referencia
      const referenceDate = acta.sentForApprovalAt ? acta.sentForApprovalAt.toDate() : 
                           (acta.createdAt ? acta.createdAt.toDate() : new Date());
      
      // Verificar si necesita enviar notificación (después de 1 día hábil)
       const shouldSendNotification = hasBusinessDaysPassed(referenceDate, 1);
        
       if (shouldSendNotification && !acta.notificationSent) {
         // Obtener el correo del responsable del proyecto
         let projectEmail = acta.customProjectEmail || await getProjectEmailWithFallback(acta.tipoProyecto);
         
         if (!projectEmail) {
           console.warn(`⚠️ No se encontró correo para el proyecto: ${acta.tipoProyecto}. Omitiendo notificación.`);
           continue;
         }
         
         console.log(`📧 Enviando notificación de recordatorio a: ${projectEmail}`);
         
         // Enviar notificación de recordatorio
         const sent = await sendApprovalNotification(projectEmail, acta);
         
         if (sent) {
           // Actualizar el acta para marcar que se envió la notificación
           await updateDoc(doc(db, 'actas', acta.id), {
             notificationSent: true,
             notificationSentAt: Timestamp.now()
           });
           
           console.log(`✅ Notificación de recordatorio enviada para acta ${acta.numeroActa}`);
           notificationsSent++;
         }
       }
     }
     
     console.log(`✅ Verificación completada. ${notificationsSent} notificaciones enviadas.`);
     return { notificationsSent };
   } catch (error) {
     console.error('Error al verificar actas pendientes:', error);
     return { notificationsSent: 0 };
   }
 };

/**

/**
 * Aprueba un acta
 * @param {string} actaId - ID del acta a aprobar
 * @param {string} approvedBy - ID del usuario que aprueba
 * @returns {Promise<boolean>} - Indica si la aprobación fue exitosa
 */
export const approveActa = async (actaId, approvedBy) => {
  try {
    const actaRef = doc(db, 'actas', actaId);
    const actaSnap = await getDoc(actaRef);
    
    if (!actaSnap.exists()) {
      console.error(`❌ Acta con ID ${actaId} no encontrada`);
      return false;
    }
    
    const actaData = actaSnap.data();
    
    // Actualizar el estado de aprobación
    await updateDoc(actaRef, {
      approvalStatus: APPROVAL_STATUS.APPROVED,
      approvedBy,
      approvedAt: Timestamp.now()
    });
    
    // Obtener los datos actualizados del acta (incluyendo encargadosAprobacion)
    const updatedActaSnap = await getDoc(actaRef);
    const updatedActaData = { id: actaId, ...updatedActaSnap.data() };
    
    // Asignar el acta al responsable del proyecto si hay encargados de aprobación
    if (updatedActaData.encargadosAprobacion && updatedActaData.encargadosAprobacion.length > 0) {
      console.log('🔄 Asignando acta al encargado de aprobación después de la aprobación...');
      await assignActaToProjectResponsible(updatedActaData);
    }
    
    // Registrar la aprobación en los logs
    const logDetails = {
      actaTitle: actaData.title || actaData.tipoActa || 'Sin título',
      previousStatus: actaData.approvalStatus || 'pendiente',
      newStatus: APPROVAL_STATUS.APPROVED
    };
    
    // Solo agregar projectId si existe
    if (actaData.projectId) {
      logDetails.projectId = actaData.projectId;
    }
    
    // Solo agregar tipoProyecto si existe
    if (actaData.tipoProyecto) {
      logDetails.tipoProyecto = actaData.tipoProyecto;
    }
    
    await logUserAction(
      LOG_ACTIONS.APPROVE,
      'acta',
      actaId,
      logDetails
    );
    
    // Enviar notificación al creador del acta
    console.log('🔍 Verificando datos para notificación:', {
      createdBy: actaData.createdBy,
      creatorEmail: actaData.creatorEmail,
      actaId: actaId
    });
    
    try {
      if (actaData.creatorEmail) {
        console.log('✅ Email del creador encontrado, enviando notificación...', {
          email: (typeof actaData.creatorEmail === 'string' ? actaData.creatorEmail.substring(0, 3) : String(actaData.creatorEmail || '').substring(0, 3)) + '***',
          actaId,
          timestamp: new Date().toISOString()
        });
        
        // Obtener el nombre del usuario que aprobó
        const approverName = await getUserDisplayName(approvedBy);
        
        const emailResult = await sendActaApprovedNotification(
          actaData.creatorEmail,
          {
            id: actaId,
            ...actaData,
            numeroActa: actaData.numeroActa,
            tipoProyecto: actaData.tipoProyecto || 'No especificado',
            tipoProyectos: actaData.tipoProyectos || [],
            productosProyecto: actaData.productosProyecto || [],
            fecha: actaData.fecha
          },
          approverName || 'Usuario del sistema'
        );
        
        if (emailResult.success) {
          console.log('📧 Notificación de aprobación enviada exitosamente:', {
            email: (typeof actaData.creatorEmail === 'string' ? actaData.creatorEmail.substring(0, 3) : String(actaData.creatorEmail || '').substring(0, 3)) + '***',
            actaId,
            requestId: emailResult.requestId,
            duration: emailResult.duration,
            timestamp: new Date().toISOString()
          });
        } else {
          console.warn('⚠️ No se pudo enviar notificación de aprobación:', {
            email: (typeof actaData.creatorEmail === 'string' ? actaData.creatorEmail.substring(0, 3) : String(actaData.creatorEmail || '').substring(0, 3)) + '***',
            actaId,
            errors: emailResult.errors,
            message: emailResult.message,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        console.warn(`⚠️ No se encontró email del creador para el acta ${actaId}`, {
          actaId,
          availableFields: Object.keys(actaData),
          timestamp: new Date().toISOString()
        });
      }
    } catch (emailError) {
      console.error('❌ Error al enviar notificación de aprobación:', {
        error: emailError.message,
        stack: emailError.stack,
        actaId,
        timestamp: new Date().toISOString()
      });
      // No fallar la aprobación por un error de email
    }
    
    console.log(`✅ Acta ${actaId} aprobada correctamente`);
    return true;
  } catch (error) {
    console.error(`❌ Error al aprobar acta ${actaId}:`, error);
    return false;
  }
};

/**
 * Configura una nueva acta con estado pendiente de aprobación
 * @param {Object} actaData - Datos del acta a crear
 * @returns {Object} - Datos del acta con el estado de aprobación añadido
 */
export const setupNewActaApproval = (actaData) => {
  return {
    ...actaData,
    // Respetar el estado de borrador si ya está definido, sino usar 'por aprobar'
    approvalStatus: actaData.approvalStatus || APPROVAL_STATUS.PENDING,
    notificationSent: false,
    // Mantener el flag de borrador si existe
    isDraft: actaData.isDraft || false
  };
};

/**
 * Asigna un acta a los usuarios responsables del proyecto (múltiples encargados)
 * @param {Object} acta - Datos del acta creada
 * @param {Array} projectNames - Nombres de proyectos resueltos (opcional)
 * @returns {Promise<boolean>} - Indica si la asignación fue exitosa
 */
export const assignActaToProjectResponsible = async (acta, projectNames = null) => {
  try {
    console.log('Asignando acta a responsables del proyecto:', acta.id);
    
    // Verificar si hay encargados de aprobación múltiples
    if (acta.encargadosAprobacion && acta.encargadosAprobacion.length > 0) {
      console.log('Procesando múltiples encargados de aprobación:', acta.encargadosAprobacion);
      
      // Procesar cada encargado de aprobación
      for (const encargado of acta.encargadosAprobacion) {
        if (encargado.email) {
          // Construir projectInfo de manera segura, evitando valores null/undefined
          const contrato = encargado.contrato || 'sin_contrato';
          const proyecto = encargado.proyecto || 'sin_proyecto';
          const projectInfo = `${contrato}|${proyecto}`;
          await processResponsibleUser(encargado.email, projectInfo, acta);
        }
      }
    } else {
      // Mantener compatibilidad con el sistema anterior (customProjectEmail)
      // Usar los nuevos campos tipoProyectos y productosProyecto si están disponibles
      // Usar nombres resueltos si se proporcionan, sino usar los del acta
      const proyectos = projectNames || (acta.tipoProyectos && acta.tipoProyectos.length > 0 
        ? acta.tipoProyectos 
        : (acta.tipoProyecto ? [acta.tipoProyecto] : []));
      
      const productos = acta.productosProyecto && acta.productosProyecto.length > 0 
        ? acta.productosProyecto 
        : (acta.productoProyecto ? [acta.productoProyecto] : []);
      
      if (proyectos.length === 0) {
        console.warn('⚠️ No se puede asignar el acta: no hay proyectos definidos');
        return false;
      }
      
      // Crear combinaciones de contrato/proyecto
      const combinaciones = [];
      if (productos.length > 0) {
        // Si hay productos, crear combinaciones contrato/producto
        proyectos.forEach(contrato => {
          productos.forEach(producto => {
            combinaciones.push(`${contrato}|${producto}`);
          });
        });
      } else {
        // Si no hay productos, usar solo contratos
        proyectos.forEach(contrato => {
          combinaciones.push(`${contrato}|sin_producto`);
        });
      }
      
      // Obtener el correo del responsable del proyecto
      // Priorizar correo personalizado si existe, sino usar el de la API
      let projectEmail = acta.customProjectEmail;
      
      if (!projectEmail) {
        // Usar la nueva función para combinaciones contrato/proyecto
        projectEmail = await getContractProjectEmailWithFallback(proyectos, productos);
      }
      
      // Si no hay correo personalizado ni de la API, no continuar
      if (!projectEmail) {
        const combinacionInfo = productos.length > 0 
          ? `proyectos: ${proyectos.join(', ')} con productos: ${productos.join(', ')}`
          : `proyectos: ${proyectos.join(', ')}`;
        console.warn(`⚠️ No se encontró correo para la combinación ${combinacionInfo}. No se puede asignar responsable.`);
        return false;
      }
      
      console.log('Usando sistema de encargado único (customProjectEmail):', projectEmail);
      console.log('Combinaciones de contrato/proyecto:', combinaciones);
      
      // Procesar cada combinación
      for (const combinacion of combinaciones) {
        await processResponsibleUser(projectEmail, combinacion, acta);
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error al asignar acta a responsables del proyecto:', error);
    return false;
  }
};

/**
 * Función auxiliar para procesar un usuario responsable
 * @param {string} email - Email del usuario responsable
 * @param {string} projectInfo - Información del proyecto
 * @param {Object} acta - Datos del acta
 */
const processResponsibleUser = async (email, projectInfo, acta) => {
  try {
    // Buscar si el usuario ya existe en la base de datos
    const usersRef = collection(db, 'useractas');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    let userId;
    
    if (querySnapshot.empty) {
      // El usuario no existe, crearlo
      console.log(`👤 Creando nuevo usuario responsable para: ${email}`);
      
      // Generar un ID único para el usuario
      userId = email.replace(/[^a-zA-Z0-9]/g, '_');
      
      // Crear el usuario en Firestore
      // Si se asigna customProjectEmail o encargadosAprobacion, dar rol APROBADOR para poder aprobar
      const assignedRole = (acta.customProjectEmail || acta.encargadosAprobacion) ? ROLES.APROBADOR : ROLES.USUARIO;
      
      await setDoc(doc(db, 'useractas', userId), {
        email: email,
        role: assignedRole,
        allowedContractProjects: [projectInfo], // Usar el sistema nuevo de combinaciones
        allowedProjects: [], // Mantener vacío para el sistema legacy
        createdAt: new Date(),
        createdBy: 'system'
      });
      
      console.log(`✅ Usuario responsable creado con rol ${assignedRole} y combinación contrato/proyecto ${projectInfo} asignado a ${email}`);
    } else {
      // El usuario existe, actualizar sus proyectos y rol si es necesario
      userId = querySnapshot.docs[0].id;
      const userData = querySnapshot.docs[0].data();
      
      // Verificar si la combinación contrato/proyecto ya está asignada
      const allowedContractProjects = userData.allowedContractProjects || [];
      const currentRole = userData.role || ROLES.USUARIO;
      
      // Determinar el nuevo rol: mantener ADMIN si ya lo tiene, sino asignar APROBADOR si hay customProjectEmail o encargadosAprobacion
      let newRole = currentRole;
      if ((acta.customProjectEmail || acta.encargadosAprobacion) && currentRole !== ROLES.ADMIN) {
        newRole = ROLES.APROBADOR;
      }
      
      const updateData = {
        updatedAt: new Date()
      };
      
      // Actualizar rol si cambió
      if (newRole !== currentRole) {
        updateData.role = newRole;
        console.log(`🔄 Actualizando rol de ${currentRole} a ${newRole} para: ${email}`);
      }
      
      // Actualizar combinaciones contrato/proyecto si es necesario
      if (!allowedContractProjects.includes(projectInfo)) {
        updateData.allowedContractProjects = [...allowedContractProjects, projectInfo];
        console.log(`✅ Combinación contrato/proyecto ${projectInfo} asignada a usuario responsable existente: ${email}`);
      } else {
        console.log(`ℹ️ La combinación contrato/proyecto ${projectInfo} ya estaba asignada al responsable: ${email}`);
      }
      
      // Aplicar actualizaciones si hay cambios
      if (Object.keys(updateData).length > 1) { // Más que solo updatedAt
        await setDoc(doc(db, 'useractas', userId), updateData, { merge: true });
      }
    }
    
    // Asignar el acta al usuario responsable si es diferente del creador
    if (acta.creatorEmail !== email) {
      // Registrar la asignación en algún lugar si es necesario
      console.log(`✅ Acta ${acta.id || acta.numeroActa} asignada al responsable del proyecto: ${email}`);
    }
    
    // Enviar notificación de creación y asignación de acta solo si no es borrador
    if (!acta.isDraft && acta.approvalStatus !== 'borrador') {
      try {
        const notificationSent = await sendActaCreationNotification(email, acta);
        if (notificationSent) {
          console.log(`📧 Notificación de creación enviada al líder: ${email}`);
        } else {
          console.warn(`⚠️ No se pudo enviar la notificación de creación al líder: ${email}`);
        }
      } catch (error) {
        console.error('Error al enviar notificación de creación:', error);
      }
    } else {
      console.log(`ℹ️ Acta guardada como borrador, no se envía notificación: ${acta.numeroActa}`);
    }
    
  } catch (error) {
    console.error('Error al procesar usuario responsable:', error);
  }
};